import { useState, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { getEmptyValues } from 'components/common/AdvanceFilterDrawer';

export const SEARCH_FIELD = 'project_q_number_or_project_j_number_or_project_name_cont';
const SEARCH_DEBOUNCE_MS = 500;

export const FILTER_FIELDS = [
  { key: 'j_number',        label: 'P/J Number',      type: 'text', paramKey: 'building_j_number_cont',      tagName: 'J#'         },
  { key: 'project_name',    label: 'Project Name',    type: 'text', paramKey: 'project_name_cont',           tagName: 'Project'    },
  { key: 'building_name',   label: 'Building Name',   type: 'text', paramKey: 'building_name_cont',          tagName: 'Building'   },
  { key: 'cse_order_entry', label: 'CSE Order Entry', type: 'text', paramKey: 'cse_order_entry_cont',        tagName: 'CSE OE'     },
  { key: 'sales_incharge',  label: 'Sales In-charge', type: 'text', paramKey: 'project_sales_incharge_cont', tagName: 'Sales'      },
  { key: 'created_by',      label: 'Created By',      type: 'text', paramKey: 'user_name_cont',              tagName: 'Created By' },
  {
    key:       'created_date',
    label:     'Created Date',
    type:      'date_range_picker',
    paramKeys: ['created_date_gteq', 'created_date_lteq'],
    tagName:   'Created',
  },
  {
    key:       'eng_requested_date',
    label:     'Entered to ENG',
    type:      'date_range_picker',
    paramKeys: ['eng_requested_date_gteq', 'eng_requested_date_lteq'],
    tagName:   'Entered to ENG',
  },
];

export const DIVIDER_AFTER = ['building_name', 'sales_incharge'];

const defaultEntryDate = () => ({
  startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
  endDate:   dayjs().format('YYYY-MM-DD'),
});

// A tag id is the field's paramKey, or paramKeys[0] for a range — removing the
// tag has to drop BOTH keys of a range, otherwise the `_lteq` half survives and
// the filter silently stays half-applied.
const paramKeysForTag = (tagId) => {
  const field = FILTER_FIELDS.find((f) => (f.paramKeys ? f.paramKeys[0] === tagId : f.paramKey === tagId));
  return field?.paramKeys ?? [tagId];
};

/**
 * Owns the filter bar shared by every tab of the Project Schedule (OE) report.
 *
 * `filterParams` carries no paging or sorting — those belong to the Detail grid
 * alone. `filterKey` is a stable string the tabs compare against their last
 * fetch, so a hidden tab refetches once when it becomes visible again instead
 * of on every keystroke.
 */
const useScheduleFilters = () => {
  const [search, setSearch]                       = useState('');
  const [committedSearch, setCommittedSearch]     = useState('');
  const [entryDate, setEntryDate]                 = useState(defaultEntryDate);
  const [salesUnitIds, setSalesUnitIds]           = useState([]);
  const [coStatusIds, setCoStatusIds]             = useState([]);
  const [buildingStatusIds, setBuildingStatusIds] = useState([]);
  const [pendingRevisionOnly, setPendingRevisionOnly] = useState(false);
  const [drawerParams, setDrawerParams]           = useState({});
  const [filterTags, setFilterTags]               = useState([]);
  const [filterValues, setFilterValues]           = useState(() => getEmptyValues(FILTER_FIELDS));
  const [showFilter, setShowFilter]               = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setCommittedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const filterParams = useMemo(() => ({
    ...(committedSearch ? { [SEARCH_FIELD]: committedSearch } : {}),
    // The REV# worklist ignores Entry Date: a REV# sits under an accepted CO#
    // whose entry date is usually months old, so the default "this month"
    // range would hide almost every REV# CSD still has to answer.
    ...(pendingRevisionOnly ? {} : {
      ...(entryDate.startDate ? { entry_date_gteq: entryDate.startDate } : {}),
      ...(entryDate.endDate ? { entry_date_lteq: entryDate.endDate } : {}),
    }),
    ...(salesUnitIds.length ? { sales_unit_id_in: salesUnitIds } : {}),
    ...(coStatusIds.length ? { co_status_in: coStatusIds } : {}),
    ...(buildingStatusIds.length ? { building_proj_status_id_in: buildingStatusIds } : {}),
    ...(pendingRevisionOnly ? { pending_revision: true } : {}),
    ...drawerParams,
  }), [committedSearch, entryDate, salesUnitIds, coStatusIds, buildingStatusIds, pendingRevisionOnly, drawerParams]);

  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);

  const applyFilter = useCallback((params, tags) => {
    setDrawerParams(params);
    setFilterTags(tags);
    setShowFilter(false);
  }, []);

  const resetFilter = useCallback(() => {
    setDrawerParams({});
    setFilterTags([]);
    setFilterValues(getEmptyValues(FILTER_FIELDS));
  }, []);

  const removeTag = useCallback((tag) => {
    const dropped = paramKeysForTag(tag.id);
    setDrawerParams((prev) => Object.fromEntries(
      Object.entries(prev).filter(([key]) => !dropped.includes(key))
    ));
    setFilterTags((prev) => prev.filter((item) => item.id !== tag.id));
  }, []);

  const setFilterValue = useCallback((key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  return {
    search,
    setSearch,
    clearSearch: useCallback(() => { setSearch(''); setCommittedSearch(''); }, []),
    entryDate,
    setEntryDate,
    salesUnitIds,
    setSalesUnitIds,
    coStatusIds,
    setCoStatusIds,
    buildingStatusIds,
    setBuildingStatusIds,
    pendingRevisionOnly,
    togglePendingRevision: useCallback(() => setPendingRevisionOnly((on) => !on), []),
    filterTags,
    filterValues,
    setFilterValue,
    showFilter,
    setShowFilter,
    applyFilter,
    resetFilter,
    removeTag,
    filterParams,
    filterKey,
  };
};

export default useScheduleFilters;
